// src/components/resources/layouts/GeneratorLayout.tsx

/**
 * Generator Layout Component
 * 
 * Provides the layout structure for generator tools, including the main content area
 * and related resources sidebar.
 * 
 * @module components/resources/layouts/GeneratorLayout
 */

'use client'

import { logClientError } from '~/lib/logger-client'
import { type ResourceTypeMap } from '~/lib/resources/types/shared/base'
import { ResourceType } from '~/lib/resources/types/shared/enums'
import {
  scoreToolForGenerator,
  scoreGlossaryForGenerator,
} from '~/lib/resources/relevance/generator-scoring'
import Image from 'next/image'
import RiCalculatorLine from '~/icons/remix/generated/RiCalculatorLine'
import RiFileTextLine from '~/icons/remix/generated/RiFileTextLine'
import { useEffect, useState, type ReactNode } from 'react'
import { fetchResourcesByTypeClient } from '~/lib/resources/api/client'
import { Breadcrumbs } from '~/components/resources/Breadcrumbs'
import { ResourceErrorBoundary } from '~/components/resources/ResourceErrorBoundary'
import { RelatedResourcesSection } from '~/components/resources/RelatedResourcesSection'
import useScroll from '~/lib/use-scroll'
import { FAQ } from '~/components/shared/FAQ'
import { renderInlineMarkdown } from '~/lib/markdown/renderInlineMarkdown'
import { GeneratorCompanionCallout } from '~/components/resources/layouts/GeneratorCompanionCallout'
import { GeneratorGrowthCallout } from '~/components/resources/layouts/GeneratorGrowthCallout'
import { generatorHasPageUpsell } from '~/lib/resources/generator-growth-config'

/**
 * Generator type definition extending the tool resource type
 * @typedef {Object} Generator
 */
type Generator = ResourceTypeMap['tool'] & { toolType: 'generator' }
type GeneratorRelatedResourcesBundle = {
  calculators: ResourceTypeMap['tool'][]
  generators: Generator[]
  analyzers: ResourceTypeMap['tool'][]
  quizzes: ResourceTypeMap['tool'][]
  glossaryTerms: ResourceTypeMap['glossary-term'][]
}

/**
 * Props for the GeneratorLayout component
 * @interface GeneratorLayoutProps
 * @property {Generator} resource - The generator resource to display
 * @property {React.ReactNode} children - Child components to render in the layout
 * @property {boolean} [fullWidth] - Whether to use a full-width layout (true) or the standard 2/3 - 1/3 grid layout (false)
 * @property {GeneratorRelatedResourcesBundle} [relatedResources] - Optional
 *   pre-computed related-resources tree. When provided, the inner
 *   `RelatedContent` block uses it directly and skips the client-
 *   side fetch so the related-links cluster ships in the SSR HTML
 *   on first paint. Computed server-side by
 *   `computeGeneratorRelatedResources` in
 *   `~/lib/resources/api/related-resources.ts`.
 */
interface GeneratorLayoutProps {
  resource: Generator
  children: React.ReactNode
  fullWidth?: boolean
  relatedResources?: GeneratorRelatedResourcesBundle
}

/**
 * Pure view for a generator's related-resources bundle. Same split-
 * for-zero-dead-state pattern as `CalculatorLayout`'s
 * `RelatedContentView` — when the parent pre-computes, this is the
 * only component that mounts (no useState, no useEffect, no
 * AbortController).
 */
function RelatedContentView({
  resources,
}: {
  resources: GeneratorRelatedResourcesBundle
}): ReactNode {
  return (
    <RelatedResourcesSection
      groups={[
        { title: 'Related Calculators', resources: resources.calculators },
        { title: 'Related Generators', resources: resources.generators },
        { title: 'Related Analyzers', resources: resources.analyzers },
        { title: 'Related Quizzes', resources: resources.quizzes },
        { title: 'Related Terms', resources: resources.glossaryTerms },
      ]}
    />
  )
}

/**
 * Legacy client-fetch path. Mirrors
 * `computeGeneratorRelatedResources` (server) so the two surfaces
 * stay behaviourally equivalent. See `CalculatorLayout` for the
 * rationale on splitting fetch and view.
 */
function RelatedContentWithFetch({
  resource,
}: {
  resource: Generator
}): JSX.Element | null {
  const [fetchedResources, setFetchedResources] = useState<GeneratorRelatedResourcesBundle>({
    calculators: [],
    generators: [],
    analyzers: [],
    quizzes: [],
    glossaryTerms: [],
  })

  useEffect(() => {
    let isMounted = true
    const abortController = new AbortController()

    async function fetchRelated(): Promise<void> {
      try {
        // Fetch all resources
        const [tools, glossaryTerms] = await Promise.all([
          fetchResourcesByTypeClient(ResourceType.TOOL),
          fetchResourcesByTypeClient(ResourceType.GLOSSARY_TERM)
          // TODO: Replace with the server function/API route (after verifying it works as well as current implementation)
        ])

        if (!isMounted) return

        // Filter tools by type and exclude the current resource.
        // Comparing `t.id !== currentSlug && t.slug !== currentSlug`
        // misses the case where `currentSlug` happens to equal a
        // different tool's `id` — use the resource's actual id +
        // slug directly so the predicate matches the server-side
        // `computeGeneratorRelatedResources` filter exactly.
        const allTools = tools.filter((t): t is ResourceTypeMap['tool'] =>
          t.resourceType === ResourceType.TOOL &&
          t.id !== resource.id &&
          t.slug !== resource.slug
        )
        
        // Get calculators
        const calculators = allTools
          .filter((t) => t.toolType === 'calculator')
          .map(calc => ({
            tool: calc,
            score: scoreToolForGenerator(calc, resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ tool }) => tool)
          .slice(0, 3)

        // Get generators
        const generators = allTools
          .filter((t): t is Generator => t.toolType === 'generator')
          .map(generator => ({
            tool: generator,
            score: scoreToolForGenerator(generator, resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ tool }) => tool)
          .slice(0, 3)

        // Get analyzers
        const analyzers = allTools
          .filter((t) => t.toolType === 'analyzer')
          .map(analyzer => ({
            tool: analyzer,
            score: scoreToolForGenerator(analyzer, resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ tool }) => tool)
          .slice(0, 3)

        // Get quizzes
        const quizzes = allTools
          .filter((t) => t.toolType === 'quiz')
          .map(quiz => ({
            tool: quiz,
            score: scoreToolForGenerator(quiz, resource)
          }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score)
          .map(({ tool }) => tool)
          .slice(0, 3)

        // Filter and score glossary terms
        const relevantTerms = glossaryTerms
          .filter((term): term is ResourceTypeMap['glossary-term'] => 
            term.resourceType === ResourceType.GLOSSARY_TERM
          )
          .map(term => ({
            term,
            score: scoreGlossaryForGenerator(term, resource)
          }))
          .filter(({ score }) => score > 10) // Higher threshold for relevance
          .sort((a, b) => b.score - a.score)
          .map(({ term }) => term)
          .slice(0, 5) // Show more glossary terms

        if (isMounted) {
          setFetchedResources({
            calculators,
            generators,
            analyzers,
            quizzes,
            glossaryTerms: relevantTerms
          })
        }
      } catch (error: unknown) {
        if (isMounted) {
          logClientError(error, { component: 'GeneratorLayout', context: 'Error fetching related resources' })
          setFetchedResources({ calculators: [], generators: [], analyzers: [], quizzes: [], glossaryTerms: [] })
        }
      }
    }

    void fetchRelated()

    return () => {
      isMounted = false
      abortController.abort('Component unmounted')
    }
    // Scalar deps (id + slug) — see CalculatorLayout for the
    // rationale on avoiding the `resource` object reference.
  }, [resource.id, resource.slug, resource])

  return <RelatedContentView resources={fetchedResources} />
}

/**
 * Dispatch between the precomputed and fetch paths — the precomputed
 * common case never mounts the useState / useEffect / AbortController
 * machinery in `RelatedContentWithFetch`.
 */
function RelatedContent({
  resource,
  precomputed,
}: {
  resource: Generator
  precomputed?: GeneratorRelatedResourcesBundle
}): JSX.Element | null {
  if (precomputed) return <RelatedContentView resources={precomputed} />
  return <RelatedContentWithFetch resource={resource} />
}

/**
 * The generator's long-form `intro` framing, rendered BELOW the interactive
 * tool. #142 added `intro` as above-the-fold body copy, but stacked beneath the
 * `description` it pushed the tool well past the fold on mobile (#148). Moving
 * it under the generator keeps the copy on the page (server-rendered, so SEO /
 * AI-overview value is retained) while letting the controls lead. Returns null
 * for generators without an `intro`.
 */
function GeneratorIntro({ intro }: { intro?: string }): JSX.Element | null {
  if (!intro) return null
  return (
    <div className="mt-10 max-w-3xl">
      <p className="text-base leading-relaxed text-gray-600 dark:text-gray-400">
        {renderInlineMarkdown(intro)}
      </p>
    </div>
  )
}

/**
 * Renders FAQ answer text with inline markdown links when the answer is a string.
 */
function FaqAnswer({ answer }: { answer: ReactNode }): JSX.Element {
  if (typeof answer === 'string') {
    return <>{renderInlineMarkdown(answer)}</>
  }
  return <>{answer}</>
}

/**
 * A framed promo render of the generator, surfaced BELOW the interactive tool.
 *
 * Each generator (and each platform landing variant) ships a deliberately-
 * designed custom render in `images.hero` — the same asset used for the
 * `og:image` social card. Until now that imagery appeared on-page nowhere: the
 * `fullWidth` layout (which every generator uses) rendered no hero, so the
 * renders lived only in social cards / structured data. Rendering it here brings
 * that imagery back as a labelled visual and adds a real on-page image for image
 * search + the resource image-sitemap.
 *
 * The label is a neutral "Preview", not "Example output": the hero set is
 * heterogeneous — the variant `-og-image` renders are stylized promo banners and
 * a few umbrella heroes depict the tool UI — so neither is a literal export of
 * the tool's output, and "Preview" is honest across the whole set.
 *
 * Placement is intentional: BELOW the tool, preserving the #148 above-the-fold
 * ordering (controls lead; copy and imagery follow) rather than the legacy
 * above-tool hero that pushed the tool past the mobile fold. Rendered at the
 * asset's intrinsic dimensions (heroes vary between 40/21 and 16/9 across tools)
 * so nothing is cropped. Returns null if a generator has no usable hero/og asset.
 */
function GeneratorPreview({ resource }: { resource: Generator }): JSX.Element | null {
  // Prefer the hero (the asset the image-sitemap lists as the on-page image),
  // but fall through to og when hero is present-but-empty — a bare `hero?.url`
  // check would let an empty hero object mask a valid og URL.
  const img = [resource.images?.hero, resource.images?.og].find((ref) =>
    Boolean(ref?.url?.trim()),
  )
  if (!img?.url) return null
  return (
    <figure className="mt-12 max-w-3xl">
      {/* Neutral label: the live interactive tool renders directly above this on
          the detail page, so name the static render so the two don't read as
          duplicates — without claiming it's a literal output (it isn't for every
          tool; see the component doc). Kept as a label rather than a
          <figcaption> so it doesn't duplicate the image's `alt`, which carries
          the description for assistive tech + image search. */}
      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Preview
      </div>
      <Image
        src={img.url}
        alt={img.alt ?? `${resource.title} preview`}
        width={img.width ?? 2400}
        height={img.height ?? 1260}
        sizes="(max-width: 768px) 100vw, 768px"
        className="h-auto w-full rounded-2xl ring-1 ring-gray-200 dark:ring-gray-800"
      />
    </figure>
  )
}

/**
 * Main generator layout component that provides the structure for generator tools
 *
 * @param {GeneratorLayoutProps} props - Component props
 * @returns {JSX.Element} Generator layout component
 */
export function GeneratorLayout({
  resource,
  children,
  fullWidth = false,
  relatedResources,
}: GeneratorLayoutProps): JSX.Element {
  const scrolled = useScroll(50)

  return (
    <div className="min-h-dvh bg-white dark:bg-gray-950">
      {/* Fixed top navigation bar that extends full width */}
      <div className="fixed top-0 left-0 right-0 z-40">
        {/* Background that extends up */}
        <div className={`absolute inset-0 transition-opacity duration-200 ${
          scrolled
            ? 'bg-gradient-to-b from-white via-white to-white/95 dark:from-gray-950 dark:via-gray-950 dark:to-gray-950/95'
            : 'bg-gradient-to-b from-white via-white/95 to-white/80 dark:from-gray-950 dark:via-gray-950/95 dark:to-gray-950/80'
        }`} />

        {/* Space for Navbar — safe-area-aware via --site-nav-height */}
        <div className="h-[var(--site-nav-height)]" />

        {/* Breadcrumbs Container — leafLabel matches H1/canonicalTitle so
            slug-derived labels (e.g. "Email Thread Mockup Generator" from
            email-thread-mockup-generator) don't diverge after SERP retitles. */}
        <div className="relative pb-1">
          <Breadcrumbs leafLabel={resource.canonicalTitle ?? resource.title} />
        </div>
      </div>

      {/* Main Content - account for fixed header height */}
      <div className="pt-[var(--generator-header-offset)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
          {/* Hero section with title and description - always full width */}
          <div className="mb-8 sm:mb-12">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl mb-4 sm:mb-6">
              {resource.title}
            </h1>
            {/*
              The concise `description` stays above the tool as the hook; the
              longer `intro` framing is rendered BELOW the generator (see
              `GeneratorIntro`) so the interactive controls clear the fold on
              mobile instead of sitting under a wall of copy. Both strings stay
              in the SSR HTML (this client component is server-rendered for
              first paint), so no SEO/AI-overview content is lost — only
              reordered.
            */}
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-3xl">
              {resource.description}
            </p>
          </div>

          {/* Conditional layout based on fullWidth prop */}
          {fullWidth ? (
            <div className="w-full">
              {/* Full width content.

                  Entrance via the site's SSR-safe `data-reveal` mechanism
                  (globals.css + RevealBootstrap) instead of a Motion
                  `initial={{ opacity: 0 }}` entrance: Motion serializes the
                  initial state into the SSR HTML, so this wrapper hid the
                  ENTIRE generator area until hydration for no-JS / blocked-JS /
                  slow-connection visitors (`r3543164053`). `data-reveal` keeps
                  the entrance the layout has always had (restored in #223) for
                  JS+motion visitors — the hidden start state lives only inside
                  a `(scripting: enabled) and (prefers-reduced-motion:
                  no-preference)` media rule — while the server / no-JS /
                  reduced-motion output stays fully visible.
                  `--reveal-translate` preserves the 20px fade-up vector. */}
              <div
                data-reveal
                style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
              >
                <div className="rounded-2xl">
                  <div className="rounded-lg">
                    <ResourceErrorBoundary key={resource.slug}>
                      {children}
                    </ResourceErrorBoundary>
                  </div>
                </div>
              </div>

              {/* Long-form intro framing + example render, below the tool (#148) */}
              <GeneratorIntro intro={resource.intro} />
              {resource.companionLinks && resource.companionLinks.length > 0 && (
                <GeneratorCompanionCallout links={resource.companionLinks} resourceSlug={resource.slug} />
              )}
              {generatorHasPageUpsell(resource.slug) && (
                <GeneratorGrowthCallout resourceSlug={resource.slug} />
              )}
              <GeneratorPreview resource={resource} />

              {/* Related resources below - full width. SSR-safe reveal (see
                  the tool wrapper above); revealed on scroll-into-view, which
                  supersedes the old mount-time stagger delay. */}
              <div
                data-reveal
                style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
                className="mt-16"
              >
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
                      <RiFileTextLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Free Generator
                      </h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        No sign up required. Generate content as much as you need.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-500/10">
                      <RiCalculatorLine className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Customizable Output
                      </h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Tailor the generated content to your specific needs.
                      </p>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                {resource.faqs && resource.faqs.length > 0 && (
                  <div
                    data-reveal
                    style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
                    className="mt-16"
                  >
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                      Frequently Asked Questions
                    </h2>
                    <FAQ
                      items={resource.faqs.map((item) => ({
                        ...item,
                        answer: <FaqAnswer answer={item.answer} />,
                      }))}
                      type="single"
                      title=""
                      disableSchema={true}
                    />
                  </div>
                )}

                {/* Related resources */}
                <RelatedContent
                  resource={resource}
                  precomputed={relatedResources}
                />
              </div>
            </div>
          ) : (
            /*
              Legacy grid layout for non-full-width generators. NOTE: every
              generator currently routes through `fullWidth` (see
              `fullWidthGenerators` in the `[slug]` route), so this branch is
              effectively unused today. The tool (`children`) leads; the example
              render and long-form copy follow below it via `GeneratorPreview` /
              `GeneratorIntro`, matching the `fullWidth` branch's #148-safe
              ordering (the previous above-tool `h-48` hero pushed the controls
              past the mobile fold). A new generator should still be added to
              `fullWidthGenerators`.
            */
            <div className="grid lg:grid-cols-3 gap-12 items-start">
              {/* Left Column: Generator (2/3 width) */}
              {/* SSR-safe reveal — see the fullWidth tool wrapper above. */}
              <div
                data-reveal
                style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
                className="lg:col-span-2"
              >
                <div className="rounded-2xl">
                  <ResourceErrorBoundary key={resource.slug}>
                    {children}
                  </ResourceErrorBoundary>
                </div>

                {/* Long-form intro framing + example render, below the tool (#148) */}
                <GeneratorIntro intro={resource.intro} />
                {resource.companionLinks && resource.companionLinks.length > 0 && (
                  <GeneratorCompanionCallout links={resource.companionLinks} resourceSlug={resource.slug} />
                )}
                {generatorHasPageUpsell(resource.slug) && (
                  <GeneratorGrowthCallout resourceSlug={resource.slug} />
                )}
                <GeneratorPreview resource={resource} />

                {/* FAQ Section for non-full width layout */}
                {resource.faqs && resource.faqs.length > 0 && (
                  <div
                    data-reveal
                    style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
                    className="mt-16"
                  >
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                      Frequently Asked Questions
                    </h2>
                    <FAQ
                      items={resource.faqs.map((item) => ({
                        ...item,
                        answer: <FaqAnswer answer={item.answer} />,
                      }))}
                      type="single"
                      title=""
                      disableSchema={true}
                    />
                  </div>
                )}
              </div>

              {/* Right Column: Context & Value Props (1/3 width) */}
              {/* SSR-safe reveal — see the fullWidth tool wrapper above. */}
              <div
                data-reveal
                style={{ '--reveal-translate': '0 20px' } as React.CSSProperties}
                className="lg:sticky lg:top-[148px]"
              >
                {/* Value Props */}
                <div className="mt-12 space-y-8">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
                      <RiFileTextLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Free Generator
                      </h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        No sign up required. Generate content as much as you need.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-500/10">
                      <RiCalculatorLine className="h-5 w-5 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white">
                        Customizable Output
                      </h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        Tailor the generated content to your specific needs.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Related Content */}
                <RelatedContent
                  resource={resource}
                  precomputed={relatedResources}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
